/**
 * DO NOT EDIT — generated by `pnpm codegen:bql` from `./schema.json`.
 *
 * The BQL wire contract, as Zod v4. These schemas are the single source for both
 * runtime validation and TypeScript types — every type here comes from
 * `z.infer`, so the wire shape is never hand-mirrored.
 *
 * Keys stay `snake_case`: camel-casing would require runtime key rewriting in
 * both directions, which breaks `.parse()` and re-introduces the translation
 * layer this pipeline exists to delete.
 *
 * Optional fields are `.optional()`, not `.nullish()` — every route serializes
 * with `exclude_none`, so an optional field is omitted rather than sent as null.
 * No `.default()` anywhere: the `$defs` pool is shared between request and
 * response models, so a default that is convenient when parsing a response would
 * be a silent payload mutation on a request. UI defaults belong in the form
 * layer, supplied explicitly.
 *
 * Regenerate: re-export from the backend (`python -m airapi.queries.bql.export_schema`),
 * then run `pnpm codegen:bql`.
 */

import { z } from 'zod'

/**
 * The canonical, dialect-neutral filter operators.
 *
 * The tail of this enum (``match``/``match_phrase``/``ends_with``/``regexp``) is
 * **not** in ``DEFAULT_OPERATOR_LEGALITY``: those are full-text/pattern operators no
 * SQL adapter can honestly compile, so they are reachable only where an entity
 * *declares* them via its per-entity legality override. Membership in this enum is
 * vocabulary, not permission.
 */
export const OperatorSchema = z.enum([
	'eq',
	'ne',
	'gt',
	'gte',
	'lt',
	'lte',
	'in',
	'not_in',
	'between',
	'contains',
	'starts_with',
	'exists',
	'not_exists',
	'match',
	'match_phrase',
	'ends_with',
	'regexp',
])

/**
 * A single leaf comparison: ``field <operator> value``.
 *
 * ``value`` is operator-shaped: absent for ``exists``/``not_exists``, a list for
 * ``in``/``not_in``, a 2-tuple-ish payload for ``between``. Shape legality is a
 * validate-before-dispatch concern (enforced in the validator, not here).
 */
export const ConditionSchema = z.strictObject({
	field: z.string(),
	operator: OperatorSchema,
	value: z.unknown().optional(),
})

/**
 * Recursive AND/OR/NOT tree of nested groups and leaf conditions.
 *
 * Depth (≤ 5) is a validate-before-dispatch concern (enforced in the validator),
 * not a shape rule.
 */
export const BoolGroupSchema = z.strictObject({
	operator: z.enum(['and', 'or', 'not']),
	get clauses() {
		return z.array(z.union([BoolGroupSchema, ConditionSchema]))
	},
})

/**
 * Auto-interval bucketing: target roughly ``target`` buckets; the adapter
 * resolves the concrete interval and echoes it back via ``meta.bucket_interval``.
 */
export const TimeBucketTargetSchema = z.strictObject({
	target: z.number().int(),
})

/**
 * One grouping dimension: a plain field, or a datetime field bucketed by a
 * named interval or an auto-interval target.
 *
 * Omitting ``alias`` names the dimension after its ``field`` — the same key on
 * either data source (see ``group_by_alias``).
 */
export const GroupBySchema = z.strictObject({
	field: z.string(),
	time_bucket: z
		.union([
			z.enum(['hour', 'day', 'week', 'month', 'quarter', 'year']),
			TimeBucketTargetSchema,
		])
		.optional(),
	alias: z.string().optional(),
	limit: z.number().int().optional(),
})

/**
 * One aggregation measure. ``alias`` is REQUIRED — it names the result column
 * and is what ``order_by`` may reference for server-side top-N.
 */
export const MetricSchema = z.strictObject({
	type: z.enum(['count', 'sum', 'avg', 'min', 'max', 'count_distinct']),
	field: z.string().optional(),
	alias: z.string(),
})

/**
 * Result ordering. ``alias`` MAY name a metric alias or a ``group_by`` alias
 * → server-side ORDER BY over an aggregation, or a projected field for raw rows.
 *
 * A ``group_by`` alias names the dimension's *effective* alias — its ``alias``
 * when it declared one, otherwise its ``field``. That name is the same on either
 * data source and is knowable when the query is written.
 */
export const OrderBySchema = z.strictObject({
	alias: z.string(),
	direction: z.enum(['asc', 'desc']).optional(),
})

/**
 * Logs addressing leaf: a connector ``definition`` and an optional
 * ``event_type``. Omitting ``event_type`` addresses *all* streams of the
 * definition.
 */
export const SourceSchema = z.strictObject({
	definition: z.string(),
	event_type: z.string().optional(),
})

/**
 * Relative time window, e.g. ``{"last": "7d"}``.
 */
export const TimeLastSchema = z.strictObject({
	last: z.string(),
})

/**
 * Absolute time window. ``from`` is aliased (Python reserved word).
 */
export const TimeRangeSchema = z.strictObject({
	from: z.string(),
	to: z.string(),
})

/**
 * One query in a batch. ``query_id`` is the client correlation id (widget.id)
 * the response envelope is keyed by.
 *
 * Note the absence of ``tenant``: it is never part of the IR. Presence of
 * ``aggregations`` (with ``group_by``) selects aggregation mode; otherwise the
 * query returns projected raw rows (``select`` / entity ``view`` — never
 * ``as_dict``).
 */
export const QueryDefinitionSchema = z.strictObject({
	entity: z.string(),
	query_id: z.string(),
	view: z.enum(['list', 'detail', 'document']).optional(),
	select: z.array(z.string()).optional(),
	filter: BoolGroupSchema.optional(),
	ids: z.array(z.string()).optional(),
	aggregations: z.array(MetricSchema).optional(),
	group_by: z.array(GroupBySchema).optional(),
	order_by: OrderBySchema.optional(),
	limit: z.number().int().optional(),
	offset: z.number().int().optional(),
	time: z.union([TimeRangeSchema, TimeLastSchema]).optional(),
	sources: z.array(SourceSchema).optional(),
})

/**
 * Batch-native request body: an unsplit list of queries. Coalescing is the
 * server's job (the client-side batcher has been removed).
 *
 * This is the fully-typed shape (every ``QueryDefinition`` parsed) and is the
 * contract the wire JSON round-trips through. It is deliberately **not** what the
 * route parses at runtime — parsing the whole batch atomically would make one
 * malformed query a 400 for the entire request. The route parses ``RawBqlRequest``
 * (envelope-structural only) and the executor parses each tile independently, so a
 * per-query shape fault is isolated to its own ``validation`` tile.
 */
export const BqlRequestSchema = z.object({
	queries: z.array(QueryDefinitionSchema).max(15),
})

/**
 * Fatal, structured, actionable error for one query. ``code`` drives UI:
 * ``forbidden`` | ``feature_not_entitled`` | ``validation`` | ``execution`` |
 * ``timeout``.
 * ``field`` points at the offending clause when applicable.
 */
export const QueryErrorSchema = z.object({
	code: z.enum([
		'forbidden',
		'feature_not_entitled',
		'validation',
		'execution',
		'timeout',
	]),
	message: z.string(),
	field: z.string().optional(),
})

/**
 * Failed result: a structured error, no rows.
 */
export const QueryFailureSchema = z.object({
	query_id: z.string(),
	success: z.literal(false),
	error: QueryErrorSchema,
})

/**
 * Non-fatal advisory alongside data (e.g. ``truncated_groups``).
 * Distinct from ``error`` — warnings keep the rows, errors drop them.
 */
export const QueryWarningSchema = z.object({
	code: z.enum([
		'truncated_groups',
		'unranked_group_tail',
		'estimated_distinct_count',
		'capped_total',
		'sort_not_applied',
		'unknown_field',
		'container_field',
		'shard_failure',
	]),
	field: z.string().optional(),
	sources: z.array(z.string()).optional(),
})

/**
 * Non-data result metadata. ``shown`` backs "showing first N"; truncation is
 * never silent. ``bucket_interval`` echoes the resolved auto-interval.
 *
 * ``document_population`` is the aggregation-mode **document** count — how many
 * in-scope documents populate the *top* grouped field — set only when the query
 * groups. It is deliberately distinct from the envelope ``Total``, which counts
 * **group tuples**, not documents: a consumer derives a per-value share as
 * ``bucket.count / document_population`` without the backend ever formatting a
 * percent. The denominator is the **field-having** population — a document with no
 * value for the grouped field is in no row and not in the count, on both adapters
 * (an OS ``terms`` agg never buckets such a doc; Postgres drops the NULL group and
 * counts the column non-null), so shown shares sum to ≤ 1. ``document_total``
 * counts those documents too, so the difference is the no-value count.
 */
export const ResultMetaSchema = z.object({
	bucket_interval: z.string().optional(),
	truncated: z.boolean().optional(),
	shown: z.number().int().optional(),
	document_population: z.number().int().optional(),
	document_total: z.number().int().optional(),
})

/**
 * Honest total. ``relation`` distinguishes an exact count from a lower
 * bound — kills the legacy histogram-sum total hack; a ``track_total_hits``-capped
 * count is reported as ``gte``, never as if exact. On an aggregation it counts the
 * groups left after the ``group_by[].limit`` cuts, before the ``limit`` row cap;
 * when a level cut dropped groups it is ``gte`` on every adapter.
 */
export const TotalSchema = z.object({
	value: z.number().int(),
	relation: z.enum(['eq', 'gte']),
})

/**
 * Successful result: flat rows keyed by ``select`` path (nesting is
 * client-side reconstruction), honest total, meta, warnings.
 */
export const QuerySuccessSchema = z.object({
	query_id: z.string(),
	success: z.literal(true),
	rows: z.array(z.record(z.string(), z.unknown())),
	total: TotalSchema,
	meta: ResultMetaSchema.optional(),
	warnings: z.array(QueryWarningSchema).optional(),
	labels: z.record(z.string(), z.record(z.string(), z.string())).optional(),
})

/**
 * Batch response: one envelope per query, each carrying its ``query_id`` key.
 */
export const BqlResponseSchema = z.object({
	results: z.array(
		z.discriminatedUnion('success', [QuerySuccessSchema, QueryFailureSchema]),
	),
})

/**
 * Declared support for ``view: "document"`` (whole source documents).
 *
 * **Present ⇒ the mode is available on this entity**; absent (``None``) ⇒ asking for
 * it is a ``validation`` rejection. Advertised rather than inferred so a client can
 * tell which entities offer a raw-document drawer without probing, and so the
 * availability rule lives in the descriptor the validator already reads instead of
 * being re-derived per call site.
 */
export const DocumentViewSchema = z.object({
	max_limit: z.number().int(),
})

/**
 * Per-field slot eligibility. For dynamic-field sources (logs) the descriptor
 * sends the ``"type-derived"`` sentinel instead of an explicit object, except for a
 * full-text-only field (``keyword_enabled`` false), which sends this object.
 */
export const CapabilitiesSchema = z.object({
	filterable: z.boolean(),
	aggregatable: z.boolean(),
	groupable: z.boolean(),
})

/**
 * Supported field data types for queryable entities.
 */
export const FieldTypeSchema = z.enum([
	'string',
	'integer',
	'float',
	'boolean',
	'datetime',
	'uuid',
	'json',
	'ip',
	'array',
])

/**
 * Field values come from a fixed enum list.
 */
export const ValueSourceEnumSchema = z.object({
	kind: z.literal('enum'),
	list: z.array(z.unknown()),
})

/**
 * Field values are discovered dynamically via the common-values query path.
 */
export const ValueSourceQuerySchema = z.object({
	kind: z.literal('query'),
	via: z.literal('common-values').optional(),
})

/**
 * One queryable field as the server declares it.
 */
export const FieldDescriptorSchema = z.object({
	id: z.string(),
	type: FieldTypeSchema,
	label: z.string(),
	projectable: z.boolean(),
	capabilities: z.union([CapabilitiesSchema, z.literal('type-derived')]),
	operators: z.array(OperatorSchema).optional(),
	value_source: z
		.discriminatedUnion('kind', [ValueSourceEnumSchema, ValueSourceQuerySchema])
		.optional(),
	keyword_enabled: z.boolean().optional(),
	value_pickable: z.boolean().optional(),
	description: z.string().optional(),
	identities: z.array(z.string()).optional(),
	multi_valued: z.boolean().optional(),
})

/**
 * Declared paging capabilities of the entity's adapter.
 */
export const PaginationSchema = z.object({
	offset: z.boolean().optional(),
	max_offset: z.number().int().optional(),
})

/**
 * Named view presets: the field-id sets for ``list`` and ``detail``.
 */
export const ProjectionsSchema = z.object({
	list: z.array(z.string()),
	detail: z.array(z.string()),
})

/**
 * Optional entitlement gate — reuses the existing SKU / feature-flag
 * systems. Drives a distinct ``feature_not_entitled`` code (upsell, not
 * ask-admin).
 */
export const RequiredFeatureSchema = z.object({
	kind: z.enum(['sku', 'flag']),
	name: z.string(),
})

/**
 * Declared support for the ``/bql/values`` route on this entity.
 *
 * **Default off**: absent (``None``) ⇒ no field on this entity is value-pickable and
 * a client should render a free-text input rather than probing per field and getting
 * a rejection each time. Present ⇒ the route answers here, under the terms below.
 *
 * Advertised rather than inferred because the two tiers answer *different questions*
 * and a client cannot tell them apart from the field list alone: a governed entity
 * answers only for fields carrying ``value_source = {kind:"query"}``, while the open
 * tier answers for any field it accepts at all.
 */
export const ValueDiscoverySchema = z.object({
	any_field: z.boolean().optional(),
	max_size: z.number().int(),
})

/**
 * A queryable entity's full server-authoritative descriptor.
 */
export const EntityDescriptorSchema = z.object({
	fields: z.array(FieldDescriptorSchema),
	addressing: z.record(z.string(), z.unknown()).optional(),
	time_anchor: z.string().optional(),
	pagination: PaginationSchema,
	projections: ProjectionsSchema,
	document_view: DocumentViewSchema.optional(),
	value_discovery: ValueDiscoverySchema.optional(),
	operator_legality: z
		.partialRecord(FieldTypeSchema, z.array(OperatorSchema))
		.optional(),
	metric_legality: z
		.partialRecord(
			FieldTypeSchema,
			z.array(z.enum(['count', 'sum', 'avg', 'min', 'max', 'count_distinct'])),
		)
		.optional(),
	regexp_max_pattern_length: z.number().int().optional(),
	max_aggregation_buckets: z.number().int().optional(),
	identity_field: z.string().optional(),
	dynamic_fields: z.boolean().optional(),
	dynamic_fields_route: z.string().optional(),
	required_permission: z.string(),
	required_feature: RequiredFeatureSchema.optional(),
})

/**
 * The ``GET /bql/schema`` payload: entity descriptors + the shared,
 * server-authoritative operator-legality table.
 */
export const BqlSchemaSchema = z.object({
	entities: z.record(z.string(), EntityDescriptorSchema),
	operator_legality: z
		.partialRecord(FieldTypeSchema, z.array(OperatorSchema))
		.optional(),
})

/**
 * The value picker's data: a field's distinct in-use values, plus whether the
 * list was capped (so the FE never presents a truncated list as exhaustive).
 */
export const CommonValuesResultSchema = z.object({
	entity: z.string(),
	field: z.string(),
	values: z.array(z.unknown()),
	truncated: z.boolean(),
	labels: z.record(z.string(), z.string()).optional(),
	counts: z.record(z.string(), z.number().int()).optional(),
	count_basis: z.enum(['sample', 'exact']).optional(),
})

/**
 * A source that did not resolve — its error, and no fields field to leave
 * undefined. A **documented outcome to render**, not a fault: a partially
 * resolvable selection shows the fields it has and says what is missing.
 */
export const FieldsSectionFailureSchema = z.object({
	source: z.string(),
	success: z.literal(false),
	error: QueryErrorSchema,
})

/**
 * A source that resolved — its fields, and no error field to leave undefined.
 */
export const FieldsSectionSuccessSchema = z.object({
	source: z.string(),
	success: z.literal(true),
	fields: z.array(FieldDescriptorSchema),
})

/**
 * One requested source's outcome, discriminated on `success`: exactly one of `fields` / `error` is present, and which one is readable from the tag without inspecting the payload. `success: true` with an empty `fields` is a legitimate empty discovery, not a failure.
 */
export const FieldsSectionSchema = z.discriminatedUnion('success', [
	FieldsSectionSuccessSchema,
	FieldsSectionFailureSchema,
])

/**
 * ``{sources: {"<definition>:<event_type>": FieldsSection}}``.
 *
 * Keyed by the source label so the FE resolves a section in one lookup, which is the
 * point of collapsing a fan-out into a single request. Every requested source is a
 * key, always — a silently dropped source is unrepresentable in this shape.
 */
export const FieldsResponseSchema = z.object({
	sources: z.record(z.string(), FieldsSectionSchema),
})

/**
 * One request-body fault of a ``POST /bql`` envelope 400.
 */
export const RouteFieldErrorSchema = z.object({
	field: z.string(),
	message: z.string(),
})

/**
 * What a route error says about its cause.
 */
export const RouteErrorContextSchema = z.object({
	code: z.enum([
		'forbidden',
		'feature_not_entitled',
		'validation',
		'execution',
	]),
	field: z.string().optional(),
	field_errors: z.array(RouteFieldErrorSchema).optional(),
})

export const RouteErrorDetailSchema = z.object({
	message: z.string(),
	context: RouteErrorContextSchema,
})

/**
 * The body of a whole-request error on a BQL route: a 4xx/5xx on
 * ``/bql/values`` and ``/bql/fields``, and the envelope 400 of ``POST /bql``.
 * The platform ``GenericErrorResponse`` shape: the text is at ``error.message``
 * and the rest at ``error.context``.
 */
export const RouteErrorSchema = z.object({
	error: RouteErrorDetailSchema,
})

// Wire roots — the 6 request and response shapes the routes exchange:
//   - BqlRequestSchema
//   - BqlResponseSchema
//   - BqlSchemaSchema
//   - FieldsResponseSchema
//   - CommonValuesResultSchema
//   - RouteErrorSchema

export type BoolGroup = z.infer<typeof BoolGroupSchema>
export type BqlRequest = z.infer<typeof BqlRequestSchema>
export type BqlResponse = z.infer<typeof BqlResponseSchema>
export type BqlSchema = z.infer<typeof BqlSchemaSchema>
export type Capabilities = z.infer<typeof CapabilitiesSchema>
export type CommonValuesResult = z.infer<typeof CommonValuesResultSchema>
export type Condition = z.infer<typeof ConditionSchema>
export type DocumentView = z.infer<typeof DocumentViewSchema>
export type EntityDescriptor = z.infer<typeof EntityDescriptorSchema>
export type FieldDescriptor = z.infer<typeof FieldDescriptorSchema>
export type FieldType = z.infer<typeof FieldTypeSchema>
export type FieldsResponse = z.infer<typeof FieldsResponseSchema>
export type FieldsSection = z.infer<typeof FieldsSectionSchema>
export type FieldsSectionFailure = z.infer<typeof FieldsSectionFailureSchema>
export type FieldsSectionSuccess = z.infer<typeof FieldsSectionSuccessSchema>
export type GroupBy = z.infer<typeof GroupBySchema>
export type Metric = z.infer<typeof MetricSchema>
export type Operator = z.infer<typeof OperatorSchema>
export type OrderBy = z.infer<typeof OrderBySchema>
export type Pagination = z.infer<typeof PaginationSchema>
export type Projections = z.infer<typeof ProjectionsSchema>
export type QueryDefinition = z.infer<typeof QueryDefinitionSchema>
export type QueryError = z.infer<typeof QueryErrorSchema>
export type QueryFailure = z.infer<typeof QueryFailureSchema>
export type QuerySuccess = z.infer<typeof QuerySuccessSchema>
export type QueryWarning = z.infer<typeof QueryWarningSchema>
export type RequiredFeature = z.infer<typeof RequiredFeatureSchema>
export type ResultMeta = z.infer<typeof ResultMetaSchema>
export type RouteError = z.infer<typeof RouteErrorSchema>
export type RouteErrorContext = z.infer<typeof RouteErrorContextSchema>
export type RouteErrorDetail = z.infer<typeof RouteErrorDetailSchema>
export type RouteFieldError = z.infer<typeof RouteFieldErrorSchema>
export type Source = z.infer<typeof SourceSchema>
export type TimeBucketTarget = z.infer<typeof TimeBucketTargetSchema>
export type TimeLast = z.infer<typeof TimeLastSchema>
export type TimeRange = z.infer<typeof TimeRangeSchema>
export type Total = z.infer<typeof TotalSchema>
export type ValueDiscovery = z.infer<typeof ValueDiscoverySchema>
export type ValueSourceEnum = z.infer<typeof ValueSourceEnumSchema>
export type ValueSourceQuery = z.infer<typeof ValueSourceQuerySchema>

/**
 * Validator bounds the backend enforces as plain module constants. They are
 * invisible to `model_json_schema()`, so the export script emits them as data.
 */
export const LIMIT_MIN = 1
export const LIMIT_MAX = 1000
export const MAX_FILTER_DEPTH = 5
